import { useState, useEffect } from "react";
import { debounce } from "../utils/debounce";

/**
 * Custom hook for detecting if the current device is mobile based on screen width
 * Uses debouncing to optimize performance during window resize events
 * @param breakpoint - The width breakpoint to determine mobile vs desktop (default: 768px)
 * @returns boolean indicating if the current device is mobile
 */
export const useIsMobile = (breakpoint: number = 768): boolean => {
  // Initialize with a function to check immediately on client
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    // During SSR, return false
    if (typeof window === "undefined") {
      return false;
    }
    // On client, check immediately
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    // Initial check (in case breakpoint prop changed)
    checkDevice();

    // Debounce resize handler to avoid excessive re-renders during window resizing
    const debouncedCheck = debounce(checkDevice, 150);

    // Add event listener for window resize
    window.addEventListener("resize", debouncedCheck);

    // Cleanup event listener on unmount
    return () => window.removeEventListener("resize", debouncedCheck);
  }, [breakpoint]);

  return isMobile;
};
